import { ChartCard } from './ChartCard';
import { chartToolbarButtonStyle } from './chartToolbarButtonStyle';
import { ChartReferenceGuide } from './ChartReferenceGuide';
import { ChartGuideButton } from './ChartGuideButton';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IoCloseOutline, IoInformationCircleOutline } from 'react-icons/io5';
import { analyze, home, type AdvancedPerformanceResponse, type HomeData } from '../../services/api';
import { HomeTrendCard } from '../HomeTrendCard';
import { NmLoader } from '../NmLoadingOverlay';
import { ReadinessCard } from '../ReadinessCard';
import { type PdcPoint, type PeakType } from './PdcCurveChart';
import { DashboardPerformanceCurve as PerformanceCurvePlot } from './DashboardPerformanceCurve';
import { useDashboardCurveComparison } from '../../hooks/useDashboardCurveComparison';
import { secToHm } from '../../utils/homeHelpers';

export type PerformanceInsightType =
  | 'power_duration'
  | 'estimated_ftp'
  | 'readiness'
  | 'compliance'
  | 'durability'
  | 'w_prime'
  | 'decoupling'
  | 'heatmap'
  | 'scatter'
  | 'formula'
  | 'peak_trends';

/** Widgets are user-managed: every card can be removed and restored from the library. */
export const ALWAYS_ON_PERFORMANCE_INSIGHTS: PerformanceInsightType[] = [];

export const OPTIONAL_PERFORMANCE_INSIGHTS: PerformanceInsightType[] = [
  'power_duration',
  'peak_trends',
  'estimated_ftp',
  'durability',
  'w_prime',
];

export const SUPPORTED_PERFORMANCE_INSIGHTS: PerformanceInsightType[] = [
  ...ALWAYS_ON_PERFORMANCE_INSIGHTS,
  ...OPTIONAL_PERFORMANCE_INSIGHTS,
];

export const DEFAULT_PERFORMANCE_INSIGHTS: PerformanceInsightType[] = [
  'power_duration',
  'peak_trends',
];

export function isAlwaysOnInsight(type: PerformanceInsightType): boolean {
  return ALWAYS_ON_PERFORMANCE_INSIGHTS.includes(type);
}

/** Merge pinned widgets into a stored/user selection (order: always-on, then the rest). */
export function ensureAlwaysOnInsights(active: PerformanceInsightType[]): PerformanceInsightType[] {
  const rest = active.filter((type) => !isAlwaysOnInsight(type));
  return [...ALWAYS_ON_PERFORMANCE_INSIGHTS, ...rest];
}

export interface PerformanceInsightDefinition {
  type: PerformanceInsightType;
  category: 'Performance' | 'Recovery' | 'Planning' | 'Summary';
  titleKey: string;
  descriptionKey: string;
  dataKey: string;
}

export const PERFORMANCE_INSIGHT_DEFS: PerformanceInsightDefinition[] = [
  {
    type: 'power_duration',
    category: 'Performance',
    titleKey: 'charts.workspace.widgets.powerDuration.title',
    descriptionKey: 'charts.workspace.widgets.powerDuration.description',
    dataKey: 'charts.workspace.data.completedActivities',
  },
  {
    type: 'peak_trends',
    category: 'Performance',
    titleKey: 'coach.peakTrends.title',
    descriptionKey: 'coach.peakTrends.subtitle',
    dataKey: 'charts.workspace.data.completedActivities',
  },
  {
    type: 'estimated_ftp',
    category: 'Performance',
    titleKey: 'charts.workspace.widgets.estimatedFtp.title',
    descriptionKey: 'charts.workspace.widgets.estimatedFtp.description',
    dataKey: 'charts.workspace.data.estimatedFtp',
  },
  {
    type: 'readiness',
    category: 'Recovery',
    titleKey: 'charts.workspace.widgets.readiness.title',
    descriptionKey: 'charts.workspace.widgets.readiness.description',
    dataKey: 'charts.workspace.data.wellness',
  },
  {
    type: 'compliance',
    category: 'Planning',
    titleKey: 'charts.workspace.widgets.compliance.title',
    descriptionKey: 'charts.workspace.widgets.compliance.description',
    dataKey: 'charts.workspace.data.planAndActual',
  },
  {
    type: 'durability',
    category: 'Performance',
    titleKey: 'charts.workspace.roadmap.durability.title',
    descriptionKey: 'charts.workspace.roadmap.durability.description',
    dataKey: 'charts.workspace.data.streams',
  },
  {
    type: 'w_prime',
    category: 'Performance',
    titleKey: 'charts.workspace.roadmap.wPrime.title',
    descriptionKey: 'charts.workspace.roadmap.wPrime.description',
    dataKey: 'charts.workspace.data.streams',
  },
  {
    type: 'decoupling',
    category: 'Performance',
    titleKey: 'charts.workspace.roadmap.decoupling.title',
    descriptionKey: 'charts.workspace.roadmap.decoupling.description',
    dataKey: 'charts.workspace.data.streams',
  },
  {
    type: 'heatmap',
    category: 'Planning',
    titleKey: 'charts.workspace.roadmap.heatmap.title',
    descriptionKey: 'charts.workspace.roadmap.heatmap.description',
    dataKey: 'charts.workspace.data.calendar',
  },
];

interface PerformanceInsightWidgetsProps {
  /** Render individual cards inside a parent chart grid. */
  layout?: 'section' | 'cards';
  renderCards?: (cards: Record<string, React.ReactNode>) => React.ReactNode;
  userId: number;
  active: PerformanceInsightType[];
  startDate: string;
  endDate: string;
  onRemove: (type: PerformanceInsightType) => void;
  onWorkoutClick?: (workoutId: number) => void;
}

function toPdcPoints(curve: Array<{
  key: string;
  interval: number;
  label: string;
  value: number;
  workout_id?: number;
}> | undefined): PdcPoint[] {
  return (curve ?? [])
    .map((point) => ({
      key: point.key,
      interval: Number(point.interval),
      label: point.label,
      value: Number(point.value),
      workoutId: point.workout_id,
    }))
    .filter((point) => (
      Number.isFinite(point.interval)
      && Number.isFinite(point.value)
      && point.value > 0
      && point.interval > 0
    ))
    .sort((a, b) => a.interval - b.interval);
}

function WidgetGuideModal({
  type,
  open,
  onClose,
  title,
  paragraphs,
}: {
  type: string;
  open: boolean;
  onClose: () => void;
  title: string;
  paragraphs: string[];
}) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="performance-widget-guide__overlay animate-fadein"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="performance-widget-guide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="performance-widget-guide-title"
      >
        <div className="performance-widget-guide__header">
          <h2 id="performance-widget-guide-title" className="performance-widget-guide__title">
            {title}
          </h2>
          <button
            type="button"
            className="performance-widget-guide__close"
            onClick={onClose}
            aria-label={t('charts.workspace.advanced.ftpGuide.close')}
          >
            <IoCloseOutline aria-hidden="true" />
          </button>
        </div>
        <div className="performance-widget-guide__body">
          {paragraphs.map((text) => (
            <p key={text} className="performance-widget-guide__paragraph">{text}</p>
          ))}
          <ChartReferenceGuide type={type} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

function WidgetShell({
  title,
  description,
  onRemove,
  children,
  className = '',
  removable = true,
  guideParagraphs,
  guideTitle,
  guideOpenLabel,
  guideType,
}: {
  title: string;
  description: string;
  onRemove: () => void;
  children: React.ReactNode;
  className?: string;
  /** When false, hide the edit-mode remove control (pinned / always-on widgets). */
  removable?: boolean;
  /** When set, description is hidden on the card and shown in a guide modal with these paragraphs. */
  guideParagraphs?: string[];
  guideTitle?: string;
  guideOpenLabel?: string;
  guideType: string;
}) {
  const { t } = useTranslation();
  const [guideOpen, setGuideOpen] = useState(false);
  const showGuide = Array.isArray(guideParagraphs) && guideParagraphs.length > 0;

  return (
    <ChartCard title={title} className={`performance-widget ${className}`}
      onRemove={removable ? onRemove : undefined}
      guide={showGuide ? <button type="button" style={chartToolbarButtonStyle}
        onClick={() => setGuideOpen(true)}
        aria-label={guideOpenLabel || t('charts.workspace.advanced.ftpGuide.open')}
        title={guideOpenLabel || t('charts.workspace.advanced.ftpGuide.open')}>
        <IoInformationCircleOutline aria-hidden="true" />
      </button> : <ChartGuideButton type={guideType} title={title}
        paragraphs={guideType === 'power_duration' ? undefined : [description, t('chartGuide.content.general.use'), t('chartGuide.content.general.note')]} />}
    >
      {children}
      {showGuide ? (
        <WidgetGuideModal
          type={guideType}
          open={guideOpen}
          onClose={() => setGuideOpen(false)}
          title={guideTitle || title}
          paragraphs={guideParagraphs}
        />
      ) : null}
    </ChartCard>
  );
}

function EmptyWidget({ message }: { message: string }) {
  return <div className="performance-widget__empty">{message}</div>;
}

function ComplianceWidget({ data }: { data: HomeData | null }) {
  const { t } = useTranslation();
  const weekly = data?.current_week_summary?.['weekly-summary'];
  const rows = useMemo(() => {
    if (!weekly) return [];
    return [
      {
        label: t('charts.workspace.compliance.duration'),
        actual: weekly.totalTime,
        planned: weekly.totalTimePlanned,
        displayActual: secToHm(weekly.totalTime),
        displayPlanned: secToHm(weekly.totalTimePlanned),
      },
      {
        label: t('charts.workspace.compliance.distance'),
        actual: weekly.totalDistance,
        planned: weekly.totalDistancePlanned,
        displayActual: `${(weekly.totalDistance / 1000).toFixed(1)} km`,
        displayPlanned: `${(weekly.totalDistancePlanned / 1000).toFixed(1)} km`,
      },
      {
        label: 'TSS',
        actual: weekly.totalTSS,
        planned: weekly.totalTSSPlanned,
        displayActual: String(Math.round(weekly.totalTSS)),
        displayPlanned: String(Math.round(weekly.totalTSSPlanned)),
      },
    ];
  }, [weekly, t]);

  if (!rows.length) return <EmptyWidget message={t('charts.workspace.noPlanningData')} />;

  return (
    <div className="performance-compliance">
      {rows.map((row) => {
        const pct = row.planned > 0 ? Math.round((row.actual / row.planned) * 100) : 0;
        return (
          <div className="performance-compliance__row" key={row.label}>
            <div className="performance-compliance__labels">
              <span>{row.label}</span>
              <strong>{Math.min(999, pct)}%</strong>
            </div>
            <div className="performance-compliance__track">
              <div
                className="performance-compliance__bar"
                style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
              />
              {pct > 100 && <span className="performance-compliance__over">+{pct - 100}%</span>}
            </div>
            <div className="performance-compliance__values">
              <span>{t('charts.workspace.compliance.completed')}: {row.displayActual}</span>
              <span>{t('charts.workspace.compliance.planned')}: {row.displayPlanned}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Evenly spaced axis ticks (always includes first + last). Caps so MM-DD labels stay readable on ~350px. */
function pickMiniChartLabelIndices(count: number, maxVisible = 4): number[] {
  if (count <= 0) return [];
  if (count <= maxVisible) return Array.from({ length: count }, (_, i) => i);
  const slots = Math.max(2, maxVisible);
  const picked = new Set<number>();
  for (let i = 0; i < slots; i += 1) {
    picked.add(Math.round((i * (count - 1)) / (slots - 1)));
  }
  return Array.from(picked).sort((a, b) => a - b);
}

function MiniLineChart({
  values,
  labels,
  threshold,
  color = '#8258e8',
}: {
  values: Array<number | null>;
  labels?: string[];
  threshold?: number;
  color?: string;
}) {
  const finite = values.filter((value): value is number => value != null && Number.isFinite(value));
  if (!finite.length) return null;
  const width = 620;
  const height = 180;
  const padX = 18;
  const padTop = 8;
  const padBottom = 22;
  const min = Math.min(...finite, threshold ?? Infinity);
  const max = Math.max(...finite, threshold ?? -Infinity);
  const span = Math.max(1, max - min);
  const plotH = height - padTop - padBottom;
  const x = (index: number) => padX + (index * (width - padX * 2)) / Math.max(1, values.length - 1);
  const y = (value: number) => padTop + plotH - ((value - min) / span) * plotH;
  const path = values
    .map((value, index) => value == null ? null : `${index === 0 ? 'M' : 'L'} ${x(index)} ${y(value)}`)
    .filter(Boolean)
    .join(' ');
  const labelCount = labels?.length ?? 0;
  const visibleLabelIndices = pickMiniChartLabelIndices(labelCount);
  const lastLabelIndex = Math.max(0, labelCount - 1);

  return (
    <div className="advanced-mini-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" className="ltr-island">
        {[0.25, 0.5, 0.75].map((ratio) => (
          <line
            key={ratio}
            x1={padX}
            x2={width - padX}
            y1={padTop + ratio * plotH}
            y2={padTop + ratio * plotH}
            className="advanced-mini-chart__grid"
          />
        ))}
        {threshold != null && (
          <line
            x1={padX}
            x2={width - padX}
            y1={y(threshold)}
            y2={y(threshold)}
            className="advanced-mini-chart__threshold"
          />
        )}
        <path d={path} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        {values.map((value, index) => value == null ? null : (
          <circle key={index} cx={x(index)} cy={y(value)} r="4" fill={color}>
            <title>{labels?.[index] ?? index}: {value}</title>
          </circle>
        ))}
        {visibleLabelIndices.map((index) => (
          <text
            key={`axis-${index}`}
            x={x(index)}
            y={height - 6}
            textAnchor={index === 0 ? 'start' : index === lastLabelIndex ? 'end' : 'middle'}
            className="advanced-mini-chart__axis-label"
          >
            {labels![index]}
          </text>
        ))}
      </svg>
    </div>
  );
}

function AdvancedEmpty({ reason }: { reason?: string | null }) {
  const { t } = useTranslation();
  return <EmptyWidget message={reason || t('charts.workspace.insufficientData')} />;
}

function DurabilityWidget({ data }: { data: AdvancedPerformanceResponse['durability'] }) {
  const { t } = useTranslation();
  if (data.status !== 'ready') return <AdvancedEmpty reason={data.reason} />;
  return (
    <>
      <div className="advanced-kpi-row">
        <span>{t('charts.workspace.advanced.duration')}: <strong>{Math.round(data.duration_seconds / 60)} min</strong></span>
        <span>{t('charts.workspace.advanced.energyAxis')}: <strong>kJ/kg</strong></span>
      </div>
      <MiniLineChart
        values={data.buckets.map((point) => point.power_watts)}
        labels={data.buckets.map((point) => String(point.kj_per_kg))}
        color="#8258e8"
      />
      <div className="advanced-note">{t('charts.workspace.advanced.durabilityMethod')}</div>
    </>
  );
}

function WPrimeWidget({
  model,
  balance,
}: {
  model: AdvancedPerformanceResponse['power_model'];
  balance: AdvancedPerformanceResponse['w_prime_balance'];
}) {
  const { t } = useTranslation();
  if (model.status !== 'ready') return <AdvancedEmpty reason={model.reason} />;
  return (
    <>
      <div className="advanced-stat-grid">
        <div><span>CP / mFTP</span><strong>{Math.round(model.cp_watts ?? 0)} W</strong></div>
        <div><span>W′</span><strong>{model.w_prime_kj?.toFixed(1)} kJ</strong></div>
        <div><span>Pmax</span><strong>{Math.round(model.p_max_watts ?? 0)} W</strong></div>
        <div><span>R²</span><strong>{model.r_squared?.toFixed(3)}</strong></div>
      </div>
      {balance.status === 'ready' ? (
        <>
          <MiniLineChart
            values={balance.points.map((point) => point.balance_percent)}
            color="#df2063"
          />
          <div className="advanced-note">
            {t('charts.workspace.advanced.minimumBalance')}: {balance.minimum_balance_kj?.toFixed(1)} kJ
          </div>
        </>
      ) : (
        <AdvancedEmpty reason={balance.reason} />
      )}
    </>
  );
}

function EstimatedFtpWidget({
  data,
  minDuration,
  onMinDurationChange,
  onWorkoutClick,
}: {
  data: AdvancedPerformanceResponse['estimated_ftp'];
  minDuration: 180 | 300 | 600 | 1200 | 1800;
  onMinDurationChange: (value: 180 | 300 | 600 | 1200 | 1800) => void;
  onWorkoutClick?: (workoutId: number) => void;
}) {
  const { t } = useTranslation();
  const controls = (
    <div className="advanced-controls advanced-controls--labeled">
      <label>
        <span>{t('charts.workspace.advanced.eftpMinDuration')}</span>
        <select
          value={minDuration}
          onChange={(event) => onMinDurationChange(Number(event.target.value) as typeof minDuration)}
        >
          {[180, 300, 600, 1200, 1800].map((seconds) => (
            <option key={seconds} value={seconds}>{Math.round(seconds / 60)} min</option>
          ))}
        </select>
      </label>
      <span className={`advanced-confidence advanced-confidence--${data.confidence.level}`}>
        {t(`charts.workspace.advanced.confidence.${data.confidence.level}`)} · {data.confidence.score}%
      </span>
    </div>
  );

  if (data.status !== 'ready') {
    return <>{controls}<AdvancedEmpty reason={data.reason} /></>;
  }

  const evidence = [...data.evidence].sort((left, right) => left.interval_seconds - right.interval_seconds);
  const delta = data.delta_from_configured_watts;
  const deltaLabel =
    delta === null ? '—' : `W ${delta > 0 ? '+' : ''}${Math.round(delta)}`;
  const watts = (value: number | null) =>
    value === null ? '—' : `W ${Math.round(value)}`;

  return (
    <>
      {controls}
      <div className="advanced-stat-grid">
        <div><span>{t('charts.workspace.advanced.configuredFtp')}</span><strong className="ltr-island">{watts(data.configured_ftp_watts)}</strong></div>
        <div><span>eFTP</span><strong className="ltr-island">{watts(data.eftp_watts)}</strong></div>
        <div><span>mFTP</span><strong className="ltr-island">{watts(data.mftp_watts)}</strong></div>
        <div><span>{t('charts.workspace.advanced.ftpDelta')}</span><strong className={`ltr-island${delta != null && delta < 0 ? ' metric-value--negative' : ''}`}>{deltaLabel}</strong></div>
      </div>
      {evidence.length > 1 ? (
        <MiniLineChart
          values={evidence.map((point) => point.estimated_ftp_watts)}
          labels={evidence.map((point) => `${Math.round(point.interval_seconds / 60)}m`)}
          color="#3977e8"
        />
      ) : null}
      {data.selected_effort ? (
        <div className="advanced-ftp-summary">
          <button
            type="button"
            onClick={() => onWorkoutClick?.(data.selected_effort!.workout_id)}
            disabled={!onWorkoutClick}
          >
            {t('charts.workspace.advanced.referenceEffort', {
              duration: Math.round(data.selected_effort.interval_seconds / 60),
              power: Math.round(data.selected_effort.peak_power_watts),
            })}
          </button>
        </div>
      ) : null}
    </>
  );
}

function DecouplingWidget({ data }: { data: AdvancedPerformanceResponse['decoupling'] }) {
  const { t } = useTranslation();
  if (data.status !== 'ready') return <AdvancedEmpty reason={data.reason} />;
  return (
    <>
      <div className="advanced-kpi-row">
        <span>{t('charts.workspace.advanced.goodBelow')} <strong>{data.good_threshold_percent}%</strong></span>
        <span>{data.points.length} {t('charts.workspace.advanced.activities')}</span>
      </div>
      <MiniLineChart
        values={data.points.map((point) => point.decoupling_percent)}
        labels={data.points.map((point) => point.date.slice(5))}
        threshold={data.good_threshold_percent}
        color="#18a98f"
      />
    </>
  );
}

function HeatmapWidget({ data }: { data: AdvancedPerformanceResponse['heatmap'] }) {
  const { t } = useTranslation();
  const max = Math.max(1, ...data.days.map((day) => day.tss_actual));
  return (
    <>
      <div className="advanced-heatmap" role="img" aria-label={t('charts.workspace.roadmap.heatmap.title')}>
        {data.days.map((day) => {
          const strength = day.tss_actual / max;
          return (
            <span
              key={day.date}
              style={{ background: day.workout_count ? `rgba(130,88,232,${0.18 + strength * 0.78})` : 'rgba(79,84,98,.08)' }}
            >
              <title>{day.date} · TSS {day.tss_actual} / {day.tss_planned}</title>
            </span>
          );
        })}
      </div>
      <div className="advanced-note">{t('charts.workspace.advanced.heatmapHint')}</div>
    </>
  );
}

function ScatterWidget({
  data,
  x,
  y,
  onXChange,
  onYChange,
}: {
  data: AdvancedPerformanceResponse['scatter'];
  x: string;
  y: string;
  onXChange: (value: string) => void;
  onYChange: (value: string) => void;
}) {
  const controls = (
    <div className="advanced-controls">
      <select value={x} onChange={(event) => onXChange(event.target.value)}>
        {data.available_channels.map((channel) => <option key={channel} value={channel}>X · {channel}</option>)}
      </select>
      <select value={y} onChange={(event) => onYChange(event.target.value)}>
        {data.available_channels.map((channel) => <option key={channel} value={channel}>Y · {channel}</option>)}
      </select>
    </div>
  );
  if (data.status !== 'ready' || !data.points.length) {
    return <>{controls}<AdvancedEmpty reason={data.reason} /></>;
  }
  const width = 620;
  const height = 220;
  const pad = 24;
  const xs = data.points.map((point) => point.x);
  const ys = data.points.map((point) => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return (
    <>
      {controls}
      <svg className="advanced-scatter" viewBox={`0 0 ${width} ${height}`} role="img">
        {data.points.map((point, index) => (
          <circle
            key={index}
            cx={pad + ((point.x - minX) / Math.max(1, maxX - minX)) * (width - pad * 2)}
            cy={height - pad - ((point.y - minY) / Math.max(1, maxY - minY)) * (height - pad * 2)}
            r="3"
          >
            <title>{x}: {point.x} · {y}: {point.y}</title>
          </circle>
        ))}
      </svg>
    </>
  );
}

function FormulaWidget({
  data,
  formula,
  onFormulaChange,
}: {
  data: AdvancedPerformanceResponse['formula'];
  formula: string;
  onFormulaChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(formula);
  useEffect(() => setDraft(formula), [formula]);
  return (
    <>
      <form
        className="advanced-formula"
        onSubmit={(event) => {
          event.preventDefault();
          onFormulaChange(draft.trim() || 'tss');
        }}
      >
        <input value={draft} onChange={(event) => setDraft(event.target.value)} dir="ltr" />
        <button type="submit">{t('charts.workspace.advanced.apply')}</button>
      </form>
      <MiniLineChart
        values={data.points.map((point) => point.value)}
        labels={data.points.map((point) => point.date.slice(5))}
        color="#f0a93b"
      />
      <div className="advanced-note" dir="ltr">{data.allowed_metrics.join(' · ')}</div>
    </>
  );
}

export function PerformanceInsightWidgets({
  layout = 'section',
  renderCards,
  userId,
  active,
  startDate,
  endDate,
  onRemove,
  onWorkoutClick,
}: PerformanceInsightWidgetsProps) {
  const { t } = useTranslation();
  const [homeData, setHomeData] = useState<HomeData | null>(null);
  const [homeLoading, setHomeLoading] = useState(false);
  const [peakType, setPeakType] = useState<PeakType>('pw_peak');
  const [paceSelected, setPaceSelected] = useState(false);
  const [currentCurve, setCurrentCurve] = useState<PdcPoint[]>([]);
  const [curveLoading, setCurveLoading] = useState(false);
  const [advancedData, setAdvancedData] = useState<AdvancedPerformanceResponse | null>(null);
  const [advancedLoading, setAdvancedLoading] = useState(false);
  const [advancedError, setAdvancedError] = useState(false);
  const [scatterX, setScatterX] = useState('power');
  const [scatterY, setScatterY] = useState('heart_rate');
  const [formula, setFormula] = useState('tss');
  const [eftpMinDuration, setEftpMinDuration] = useState<180 | 300 | 600 | 1200 | 1800>(300);

  const needsHome = active.includes('readiness') || active.includes('compliance');
  const needsCurve = active.includes('power_duration');
  const curveComparison = useDashboardCurveComparison(userId, startDate, endDate, peakType, needsCurve, paceSelected ? 3 : undefined);
  const advancedTypes: PerformanceInsightType[] = ['estimated_ftp', 'durability', 'w_prime', 'decoupling', 'heatmap'];
  const needsAdvanced = active.some((type) => advancedTypes.includes(type));

  useEffect(() => {
    if (!needsHome) return;
    let cancelled = false;
    setHomeLoading(true);
    home.getHomeData(userId)
      .then((response) => {
        if (!cancelled) setHomeData(response.data.data);
      })
      .catch(() => {
        if (!cancelled) setHomeData(null);
      })
      .finally(() => {
        if (!cancelled) setHomeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [needsHome, userId]);

  useEffect(() => {
    if (!needsCurve) return;
    let cancelled = false;
    setCurveLoading(true);
    setCurrentCurve([]);
    analyze.getPowerDurationCurve(userId, {
      start_date: startDate,
      end_date: endDate,
      peak_type: peakType,
      workout_type_id: paceSelected ? 3 : undefined,
    })
      .then((current) => {
        if (cancelled) return;
        setCurrentCurve(toPdcPoints(current.data.data.curve));
      })
      .catch(() => {
        if (!cancelled) {
          setCurrentCurve([]);
        }
      })
      .finally(() => {
        if (!cancelled) setCurveLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [endDate, needsCurve, peakType, paceSelected, startDate, userId]);

  useEffect(() => {
    if (!needsAdvanced) return;
    let cancelled = false;
    setAdvancedLoading(true);
    setAdvancedError(false);
    analyze.getAdvancedPerformance(userId, {
      start_date: startDate,
      end_date: endDate,
      scatter_x: scatterX,
      scatter_y: scatterY,
      formula,
      eftp_min_duration: eftpMinDuration,
    })
      .then((response) => {
        if (!cancelled) setAdvancedData(response.data.data);
      })
      .catch(() => {
        if (!cancelled) {
          setAdvancedData(null);
          setAdvancedError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setAdvancedLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eftpMinDuration, endDate, formula, needsAdvanced, scatterX, scatterY, startDate, userId]);

  const widgetActive = active.filter((type) => type !== 'peak_trends');
  if (!widgetActive.length) return renderCards ? <>{renderCards({})}</> : null;

  const cards = widgetActive.map((type) => {
          const def = PERFORMANCE_INSIGHT_DEFS.find((item) => item.type === type);
          if (!def) return null;
          const common = {
            guideType: type,
            title: t(`charts.library.types.${type}.title`, {defaultValue: t(def.titleKey)}),
            description: t(`charts.library.types.${type}.description`, {defaultValue: t(def.descriptionKey)}),
            onRemove: () => onRemove(type),
            removable: true,
          };

          if (type === 'power_duration') {
            return (
              <WidgetShell key={type} {...common} className="performance-widget--wide performance-widget--curve">
                {
                  <PerformanceCurvePlot curve={currentCurve} peakType={peakType} paceSelected={paceSelected} onPaceSelectedChange={selected => {if (selected !== paceSelected) {setCurrentCurve([]); setPaceSelected(selected);}}}
                    comparison={curveComparison} loading={curveLoading}
                    onPeakTypeChange={setPeakType} onWorkoutClick={onWorkoutClick} />
                }
              </WidgetShell>
            );
          }

          if (type === 'readiness') {
            return (
              <WidgetShell key={type} {...common} className="performance-widget--wide">
                {homeLoading ? (
                  <div className="performance-widget__loader"><NmLoader /></div>
                ) : homeData?.readiness?.today ? (
                  <div className="performance-readiness-stack">
                    <ReadinessCard
                      today={homeData.readiness.today}
                      trends={homeData.today_trends}
                    />
                    <HomeTrendCard
                athleteId={userId}
                refreshKey={homeData}
                      history={homeData.readiness.history}
                      daily={homeData.trend_daily ?? []}
                      chartHeight={180}
                    />
                  </div>
                ) : (
                  <EmptyWidget message={t('charts.workspace.noReadinessData')} />
                )}
              </WidgetShell>
            );
          }

          if (type === 'durability') {
            return (
              <WidgetShell key={type} {...common}>
                {advancedLoading && !advancedData ? <div className="performance-widget__loader"><NmLoader /></div>
                  : advancedData ? <DurabilityWidget data={advancedData.durability} />
                    : <AdvancedEmpty reason={advancedError ? t('charts.workspace.advanced.loadFailed') : undefined} />}
              </WidgetShell>
            );
          }

          if (type === 'estimated_ftp') {
            const ftp = advancedData?.estimated_ftp;
            const guideParagraphs = [
              common.description,
              ...(ftp?.status === 'ready'
                ? [t(`charts.workspace.advanced.recommendation.${ftp.recommendation}`)]
                : []),
              t('charts.workspace.advanced.estimatedFtpMethod'),
              t('charts.workspace.advanced.ftpNotAutoApplied'),
            ];
            return (
              <WidgetShell
                key={type}
                {...common}
                guideTitle={t('charts.workspace.advanced.ftpGuide.title')}
                guideParagraphs={guideParagraphs}
              >
                {advancedLoading && !advancedData ? <div className="performance-widget__loader"><NmLoader /></div>
                  : advancedData?.estimated_ftp ? (
                    <EstimatedFtpWidget
                      data={advancedData.estimated_ftp}
                      minDuration={eftpMinDuration}
                      onMinDurationChange={setEftpMinDuration}
                      onWorkoutClick={onWorkoutClick}
                    />
                  ) : <AdvancedEmpty reason={t('charts.workspace.advanced.loadFailed')} />}
              </WidgetShell>
            );
          }

          if (type === 'w_prime') {
            const guideParagraphs = [
              common.description,
              t('charts.workspace.advanced.wPrimeGuide.reserve'),
              t('charts.workspace.advanced.wPrimeGuide.chart'),
              t('charts.workspace.advanced.wPrimeGuide.model'),
            ];
            return (
              <WidgetShell
                key={type}
                {...common}
                guideTitle={t('charts.workspace.advanced.wPrimeGuide.title')}
                guideOpenLabel={t('charts.workspace.advanced.wPrimeGuide.open')}
                guideParagraphs={guideParagraphs}
              >
                {advancedLoading && !advancedData ? <div className="performance-widget__loader"><NmLoader /></div>
                  : advancedData ? <WPrimeWidget model={advancedData.power_model} balance={advancedData.w_prime_balance} />
                    : <AdvancedEmpty reason={advancedError ? t('charts.workspace.advanced.loadFailed') : undefined} />}
              </WidgetShell>
            );
          }

          if (type === 'decoupling') {
            const guideParagraphs = [
              common.description,
              t('charts.workspace.advanced.decouplingGuide.metric'),
              t('charts.workspace.advanced.decouplingGuide.threshold'),
              t('charts.workspace.advanced.decouplingGuide.chart'),
            ];
            return (
              <WidgetShell
                key={type}
                {...common}
                guideTitle={t('charts.workspace.advanced.decouplingGuide.title')}
                guideOpenLabel={t('charts.workspace.advanced.decouplingGuide.open')}
                guideParagraphs={guideParagraphs}
              >
                {advancedLoading && !advancedData ? <div className="performance-widget__loader"><NmLoader /></div>
                  : advancedData ? <DecouplingWidget data={advancedData.decoupling} />
                    : <AdvancedEmpty reason={advancedError ? t('charts.workspace.advanced.loadFailed') : undefined} />}
              </WidgetShell>
            );
          }

          if (type === 'heatmap') {
            return (
              <WidgetShell key={type} {...common}>
                {advancedLoading && !advancedData ? <div className="performance-widget__loader"><NmLoader /></div>
                  : advancedData ? <HeatmapWidget data={advancedData.heatmap} />
                    : <AdvancedEmpty reason={advancedError ? t('charts.workspace.advanced.loadFailed') : undefined} />}
              </WidgetShell>
            );
          }

          if (type === 'scatter') {
            return (
              <WidgetShell key={type} {...common}>
                {advancedLoading && !advancedData ? <div className="performance-widget__loader"><NmLoader /></div>
                  : advancedData ? (
                    <ScatterWidget
                      data={advancedData.scatter}
                      x={scatterX}
                      y={scatterY}
                      onXChange={setScatterX}
                      onYChange={setScatterY}
                    />
                  ) : <AdvancedEmpty reason={advancedError ? t('charts.workspace.advanced.loadFailed') : undefined} />}
              </WidgetShell>
            );
          }

          if (type === 'formula') {
            return (
              <WidgetShell key={type} {...common}>
                {advancedLoading && !advancedData ? <div className="performance-widget__loader"><NmLoader /></div>
                  : advancedData ? <FormulaWidget data={advancedData.formula} formula={formula} onFormulaChange={setFormula} />
                    : <AdvancedEmpty reason={advancedError ? t('charts.workspace.advanced.loadFailed') : undefined} />}
              </WidgetShell>
            );
          }

          return (
            <WidgetShell key={type} {...common}>
              {homeLoading ? (
                <div className="performance-widget__loader"><NmLoader /></div>
              ) : (
                <ComplianceWidget data={homeData} />
              )}
            </WidgetShell>
          );
        });

  if (renderCards) return <>{renderCards(Object.fromEntries(widgetActive.map((type,index)=>[type,cards[index]])))}</>;

  if (layout === 'cards') return <>{cards}</>;

  return (
    <section className="performance-insights" aria-label={t('charts.workspace.insightsTitle')}>
      <div className="performance-insights__grid charts-feature-grid">{cards}</div>
    </section>
  );
}
